import HttpService from './http';
import CacheManager from './userPhoto/CacheManager';
import CircuitBreaker from './userPhoto/CircuitBreaker';
import RateLimiter from './userPhoto/RateLimiter';

/**
 * Service for fetching, caching, and managing user photos with advanced features
 * including circuit breaking, rate limiting, request deduplication, and retry logic.
 *
 * @description Provides a robust layer for user photo retrieval with built-in resilience patterns.
 * Features include LRU caching, circuit breaker for fault tolerance, rate limiting for API protection,
 * automatic retry with exponential backoff, and request deduplication to prevent duplicate API calls.
 *
 * @since 1.0.0
 */
class UserPhotoService {
  constructor() {
    this.pendingRequests = new Map();
    this.userMetadata = new WeakMap();

    this.config = {
      maxConcurrentRequests: 10,
      retryAttempts: 3,
      retryDelayMs: 1000,
      cleanupIntervalMs: 5 * 60 * 1000,
    };

    this.cacheManager = new CacheManager();
    this.circuitBreaker = new CircuitBreaker();
    this.rateLimiter = new RateLimiter();

    this.boundEventHandlers = {
      userPhotoUpdated: this._handlePhotoUpdated.bind(this),
      beforeunload: this._handleBeforeUnload.bind(this),
      visibilitychange: this._handleVisibilityChange.bind(this),
    };

    this._initializeEventListeners();
    this._startBackgroundCleanup();
  }

  /**
   * Retrieve a user's photo with caching, rate limiting, and circuit breaking.
   *
   * @description Fetches user photo data from cache or API with built-in resilience patterns.
   * Implements request deduplication to prevent multiple concurrent requests for the same user.
   *
   * @param {string} userId - The unique identifier of the user whose photo to retrieve
   * @param {boolean} isAgent - Whether the user is an AI agent (defaults to false)
   * @returns {Promise<{photoPublicUrl: string|null, expiration: string}>} Photo data with public URL and expiration (ISOString)
   * @throws {Error} When userId is missing, rate limit exceeded, or service unavailable
   */
  async getUserPhoto(userId, isAgent = false) {
    if (!userId) throw new Error('User ID is required');

    if (!this.rateLimiter.isRequestAllowed()) {
      throw new Error('Rate limit exceeded');
    }

    if (!this.circuitBreaker.isRequestAllowed()) {
      throw new Error('Service temporarily unavailable');
    }

    const cacheKey = `${isAgent ? 'agent' : 'user'}:${userId}`;
    const cachedResult = this.cacheManager.get(cacheKey);

    if (cachedResult) return cachedResult;
    // Request deduplication
    if (this.pendingRequests.has(cacheKey)) {
      return await this.pendingRequests.get(cacheKey);
    }

    const requestPromise = this._fetchFromApiWithRetry(userId, cacheKey, isAgent);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      this.circuitBreaker.recordSuccess();
      return result;
    } catch (error) {
      this.circuitBreaker.recordFailure();
      throw error;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Invalidate cached photo data for a specific user.
   *
   * @description Removes the user's photo from cache and cancels any pending requests.
   * Typically called when a user updates their profile photo.
   *
   * @param {string} userId - The unique identifier of the user whose cache to invalidate
   * @param {boolean} isAgent - Whether the user is an AI agent (defaults to false)
   * @returns {void}
   */
  invalidateUserPhoto(userId, isAgent = false) {
    if (!userId) return;
    const cacheKey = `${isAgent ? 'agent' : 'user'}:${userId}`;
    this.cacheManager.invalidate(cacheKey);
    this.pendingRequests.delete(cacheKey);
  }

  /**
   * Clear all cached photo data and cancel pending requests.
   *
   * @description Removes all entries from the cache and cancels any active HTTP requests.
   * Useful for memory cleanup or when user authentication changes.
   *
   * @returns {void}
   */
  clearCache() {
    this.cacheManager.clear();
    this.pendingRequests.forEach((_, key) => {
      if (HttpService.getInstance().isRequestActive(`/user/photo/${key}`)) {
        HttpService.getInstance().cancelRequest(`/user/photo/${key}`);
      }
    });
    this.pendingRequests.clear();
  }

  /**
   * Fetch user photo from API with exponential backoff retry logic.
   *
   * @description Implements retry mechanism with exponential backoff and jitter.
   * Skips retry for authentication errors (401, 403) and abort errors.
   * Invalidates cache on 403 to allow fresh fetch on next attempt.
   *
   * @param {string} userId - The user ID to fetch photo for
   * @param {string} cacheKey - The cache key for the request
   * @param {boolean} isAgent - Whether the user is an AI agent
   * @returns {Promise<{photoPublicUrl: string|null, expiration: string}>} Photo data from API
   * @throws {Error} When max retry attempts exceeded or permanent error occurs
   *
   * @private
   */
  async _fetchFromApiWithRetry(userId, cacheKey, isAgent = false) {
    const maxAttempts = this.config.retryAttempts;
    let lastError;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this._fetchFromApi(userId, cacheKey, isAgent);
      } catch (error) {
        lastError = error;

        if (error.status === 403) {
          this.cacheManager.invalidate(cacheKey);
          break;
        }

        if (error.name === 'AbortError' || error.status === 401 || attempt === maxAttempts) {
          break;
        }

        const delay = this.config.retryDelayMs * 2 ** (attempt - 1) + Math.random() * 1000;
        await this._delay(delay);
      }
    }

    throw lastError || new Error('Max retry attempts exceeded');
  }

  /**
   * Perform the actual API request to fetch user photo data.
   *
   * @description Makes HTTP request to retrieve user photo and handles caching logic.
   * Caches successful responses and temporary null responses for errors that can be retried.
   * Routes to appropriate endpoint based on whether the user is an AI agent.
   *
   * @param {string} userId - The user ID to fetch photo for
   * @param {string} cacheKey - The cache key for storing the result
   * @param {boolean} isAgent - Whether the user is an AI agent
   * @returns {Promise<{photoPublicUrl: string|null, expiration: string}>} Photo data from API response
   * @throws {Error} When API request fails
   *
   * @private
   */
  async _fetchFromApi(userId, cacheKey, isAgent = false) {
    try {
      const endpoint = isAgent ? `/ai/agent/photo/${userId}` : `/user/photo/${userId}`;
      const res = await HttpService.getInstance().request(endpoint);
      const data = {
        photoPublicUrl: res?.photoPublicUrl || null,
        expiration: res?.expiration || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      };

      this.cacheManager.set(cacheKey, data);
      return data;
    } catch (error) {
      if (this._canRetryError(error)) {
        const data = {
          photoPublicUrl: null,
          expiration: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
        };
        this.cacheManager.set(cacheKey, data, true);
      }
      throw error;
    }
  }

  /**
   * Handle custom userPhotoUpdated events to invalidate cache.
   *
   * @description Event handler for photo update notifications. Invalidates
   * cache entry when a user updates their profile photo.
   *
   * @param {CustomEvent} event - Custom event with userId in detail property
   * @returns {void}
   * @private
   */
  _handlePhotoUpdated(event) {
    const { userId } = event.detail || {};
    if (userId) this.invalidateUserPhoto(userId);
  }

  /**
   * Handle browser beforeunload event to perform cleanup.
   *
   * @description Cleans up resources, cancels requests, and removes event listeners
   * before the page unloads to prevent memory leaks.
   *
   * @returns {void}
   * @private
   */
  _handleBeforeUnload() {
    this._cleanup();
  }

  /**
   * Handle document visibility changes for background cleanup.
   *
   * @description Performs cache and rate limiter cleanup when document becomes hidden
   * to optimize memory usage during background operation.
   *
   * @returns {void}
   * @private
   */
  _handleVisibilityChange() {
    if (document.hidden) {
      this._performBackgroundCleanup();
    }
  }

  /**
   * Perform background maintenance tasks on cache and rate limiter.
   *
   * @description Triggers cleanup routines for cache manager and rate limiter
   * to remove expired entries and optimize memory usage.
   *
   * @returns {void}
   * @private
   */
  _performBackgroundCleanup() {
    this.cacheManager.performBackgroundCleanup();
    this.rateLimiter.cleanup();
  }

  /**
   * Create a promise that resolves after specified milliseconds.
   *
   * @description Utility function for implementing delays in retry logic
   * and batch processing to avoid overwhelming the API.
   *
   * @param {number} ms - Number of milliseconds to delay
   * @returns {Promise<void>} Promise that resolves after the delay
   * @private
   */
  _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Determine if an error is eligible for retry attempts.
   *
   * @description Checks error type and status to decide if a retry should be attempted.
   * Network errors, timeouts, and 5xx server errors can be retried.
   *
   * @param {Error} error - The error object to evaluate
   * @returns {boolean} True if the error supports retries, false otherwise
   * @private
   */
  _canRetryError(error) {
    return (
      error.name === 'NetworkError' ||
      error.name === 'TimeoutError' ||
      (error.status && error.status >= 500) ||
      !error.status
    );
  }

  /**
   * Set up event listeners for browser and custom events.
   *
   * @description Registers event handlers for photo updates, page unload,
   * and document visibility changes. Only runs in browser environment.
   *
   * @returns {void}
   * @private
   */
  _initializeEventListeners() {
    if (typeof window === 'undefined') return;
    window.addEventListener('userPhotoUpdated', this.boundEventHandlers.userPhotoUpdated);
    window.addEventListener('beforeunload', this.boundEventHandlers.beforeunload);
    window.addEventListener('visibilitychange', this.boundEventHandlers.visibilitychange);
  }

  /**
   * Start periodic background cleanup tasks.
   *
   * @description Initiates interval timer for regular cache and rate limiter maintenance.
   * Cleanup runs every 5 minutes to remove expired entries.
   *
   * @returns {void}
   * @private
   */
  _startBackgroundCleanup() {
    if (typeof window !== 'undefined') {
      this.cleanupInterval = setInterval(() => {
        this._performBackgroundCleanup();
      }, this.config.cleanupIntervalMs);
    }
  }

  /**
   * Clean up all resources, event listeners, and pending requests.
   *
   * @description Comprehensive cleanup that removes event listeners, clears intervals,
   * cancels pending HTTP requests, and clears the pending requests map.
   *
   * @returns {void}
   * @private
   */
  _cleanup() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('userPhotoUpdated', this.boundEventHandlers.userPhotoUpdated);
      window.removeEventListener('beforeunload', this.boundEventHandlers.beforeunload);
      window.removeEventListener('visibilitychange', this.boundEventHandlers.visibilitychange);
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    this.pendingRequests.forEach((_, key) => {
      if (HttpService.getInstance().isRequestActive(`/user/photo/${key}`)) {
        HttpService.getInstance().cancelRequest(`/user/photo/${key}`);
      }
    });

    this.pendingRequests.clear();
  }

  /**
   * Preload photos for multiple users in optimized batches.
   *
   * @description Efficiently preloads user photos in small batches to warm the cache
   * without overwhelming the API. Filters out already cached entries and limits
   * concurrent requests.
   *
   * @param {string[]} userIds - Array of user IDs to preload photos for
   * @param {number} [batchSize=10] - Maximum number of photos to preload in this operation
   * @returns {Promise<void>} Promise that resolves when preloading is complete
   */
  async preloadPhotos(userIds, batchSize = 10) {
    if (!Array.isArray(userIds) || userIds.length === 0) return;

    const validUserIds = userIds
      .filter((userId) => userId && !this.cacheManager.get(String(userId)))
      .slice(0, Math.min(batchSize, this.config.maxConcurrentRequests));

    if (validUserIds.length === 0) return;
    const results = [];

    for (let i = 0; i < validUserIds.length; i += 5) {
      const batch = validUserIds.slice(i, i + 5);
      const batchPromises = batch.map((userId) => this.getUserPhoto(userId).catch(() => null));
      const batchResults = await Promise.allSettled(batchPromises);

      results.push(...batchResults);

      if (i + 5 < validUserIds.length) {
        await this._delay(100);
      }
    }
  }
}

let instance;

/**
 * Get the singleton instance of UserPhotoService.
 *
 * @description Implements singleton pattern to ensure only one service instance
 * exists throughout the application lifecycle. Creates instance on first call.
 *
 * @returns {UserPhotoService} The singleton service instance
 * @static
 */
UserPhotoService.getInstance = () => {
  if (!instance) instance = new UserPhotoService();
  return instance;
};

export default UserPhotoService;
